/**
 * Collection infinite scroll (wheels + apparel).
 *
 * Progressive enhancement over the server-rendered pagination: as the shopper
 * nears the bottom we fetch the next ?page=N (which carries the current
 * sort/filter params), pull its product cards out, and append them.
 *
 * SEO is preserved — the Liquid `{% paginate %}` nav stays in the DOM (crawlable
 * ?page=N links); we just hide it visually once JS takes over. No-JS users and
 * crawlers still get real pagination.
 *
 * Back-navigation scroll restore: before leaving (pagehide) we remember how many
 * extra pages were loaded + the scroll position, keyed to this collection URL
 * (incl. sort/filter). On a Back/Forward return we re-fetch those pages and jump
 * back to the saved spot. Only on back/forward — a normal reload starts fresh.
 * bfcache "instant back" already restores the page intact, so our path only runs
 * when the browser did a real reload.
 */
(function () {
  'use strict';

  var STORE_KEY = 'cw:iscroll'; // sessionStorage slot for scroll-restore state

  function nextHref() {
    var a = document.querySelector('.cw-pagination__arrow--next[href]');
    return a ? a.getAttribute('href') : null;
  }

  function currentKey() {
    return location.pathname + location.search;
  }

  // 'navigate' | 'reload' | 'back_forward' — restore only on back/forward.
  function navType() {
    try {
      var e = performance.getEntriesByType('navigation')[0];
      if (e && e.type) return e.type;
    } catch (err) {}
    try {
      var t = performance.navigation.type;
      return t === 2 ? 'back_forward' : (t === 1 ? 'reload' : 'navigate');
    } catch (err) {}
    return 'navigate';
  }

  function readState() {
    try {
      var raw = sessionStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      return (s && s.key === currentKey() && s.pages > 0) ? s : null;
    } catch (e) { return null; }
  }

  function init() {
    var grid = document.getElementById('product-grid');
    if (!grid) return;

    var next = nextHref();
    var nav = document.querySelector('.cw-pagination');

    var pagesLoaded = 0; // extra pages appended past the server-rendered first page
    var loading = false;
    var done = false;

    // We own scroll restoration: content loads async, so the browser's automatic
    // restore would land on not-yet-loaded content. (bfcache still restores fully.)
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    // Save state on the way out so Back can restore it. pagehide fires reliably on
    // navigation away (including to a PDP) and is bfcache-safe.
    window.addEventListener('pagehide', function () {
      try {
        if (pagesLoaded > 0) {
          sessionStorage.setItem(STORE_KEY, JSON.stringify({
            key: currentKey(),
            pages: pagesLoaded,
            y: window.scrollY || window.pageYOffset || 0
          }));
        } else {
          sessionStorage.removeItem(STORE_KEY);
        }
      } catch (e) { /* storage unavailable — no restore, no harm */ }
    });

    function hideNav() { if (nav) nav.style.display = 'none'; }

    // Fetch the current `next` page, append its cards, advance `next`.
    // Resolves true if a page was appended, false if there was nothing to load.
    function fetchNext() {
      if (!next) return Promise.resolve(false);
      var url = next;
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          if (!html) { next = null; return false; }
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var incoming = doc.getElementById('product-grid');
          if (incoming) {
            var cards = incoming.querySelectorAll('.cw-product-card');
            var frag = document.createDocumentFragment();
            cards.forEach(function (c) { frag.appendChild(c); });
            grid.appendChild(frag);
          }
          pagesLoaded++;
          var na = doc.querySelector('.cw-pagination__arrow--next[href]');
          next = na ? na.getAttribute('href') : null;
          return true;
        })
        .catch(function () { next = null; return false; });
    }

    function setupObserver() {
      if (!next) return; // no more pages to load
      var sentinel = document.createElement('div');
      sentinel.className = 'cw-infinite-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.width = '100%';
      sentinel.style.height = '1px';
      grid.parentNode.insertBefore(sentinel, grid.nextSibling);

      var observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) load();
      }, { rootMargin: '800px 0px' });
      observer.observe(sentinel);

      function load() {
        if (loading || done || !next) return;
        loading = true;
        fetchNext().then(function (ok) {
          if (!ok || !next) stop();
        }).finally(function () { loading = false; });
      }

      function stop() {
        done = true;
        observer.disconnect();
        if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
      }
    }

    var state = navType() === 'back_forward' ? readState() : null;
    if (!state) { try { sessionStorage.removeItem(STORE_KEY); } catch (e) {} }

    if (!next && !state) return; // single page, nothing to restore — leave nav as-is

    hideNav();

    if (state) {
      // Restore: re-load the pages the shopper had, then jump back to their spot.
      // ponytail: sequential re-fetch (order matters for append); deep scrolls
      // re-request N pages one by one. Parallel+reorder if that ever gets slow.
      try { sessionStorage.removeItem(STORE_KEY); } catch (e) {}
      (function restoreLoop() {
        if (pagesLoaded >= state.pages || !next) {
          window.scrollTo(0, state.y);
          // second pass after layout/images settle so the offset lands true
          requestAnimationFrame(function () { window.scrollTo(0, state.y); });
          setupObserver();
          return;
        }
        fetchNext().then(restoreLoop);
      })();
    } else {
      setupObserver();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
